import React from 'react';
import { Badge, Checkbox } from '../../../../shared/components/ui';
import { GroupingField } from '../../hooks/usePendingOrders';

interface GroupingsSectionProps {
    groupingFields: Set<GroupingField>;
    toggleGrouping: (field: GroupingField) => void;
    clearGroupings: () => void;
    groupingStats: {
        couriers: number;
        shops: number;
        countries: number;
        orderTypes: number;
        teamNames: number;
    };
    getGroupingDescription: () => string;
    isInsideAccordion?: boolean;
}

export const GroupingsSection: React.FC<GroupingsSectionProps> = ({
    groupingFields,
    toggleGrouping,
    clearGroupings,
    groupingStats,
    getGroupingDescription,
    isInsideAccordion = false
}) => {
    const handleGroupingToggle = (field: GroupingField) => {
        toggleGrouping(field);
    };

    // Si está dentro del acordeón, solo mostrar el contenido sin el contenedor card
    if (isInsideAccordion) {
        return (
            <div className="mt-4">
                <div className="flex items-center justify-between mb-4">
                    {groupingFields.size > 0 && (
                        <button
                            onClick={clearGroupings}
                            className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors ml-auto"
                        >
                            Limpiar
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Agrupar por Mensajería */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${groupingFields.has('courier')
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-accent/30'
                        }`}>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={groupingFields.has('courier')}
                                onCheckedChange={() => handleGroupingToggle('courier')}
                            />
                            <label
                                className="text-sm font-medium cursor-pointer"
                                onClick={() => handleGroupingToggle('courier')}
                            >
                                Por Mensajería
                            </label>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {groupingStats.couriers}
                        </Badge>
                    </div>

                    {/* Agrupar por Tienda */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${groupingFields.has('shop')
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-accent/30'
                        }`}>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={groupingFields.has('shop')}
                                onCheckedChange={() => handleGroupingToggle('shop')}
                            />
                            <label
                                className="text-sm font-medium cursor-pointer"
                                onClick={() => handleGroupingToggle('shop')}
                            >
                                Por Tienda
                            </label>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {groupingStats.shops}
                        </Badge>
                    </div>

                    {/* Agrupar por País */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${groupingFields.has('country')
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-accent/30'
                        }`}>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={groupingFields.has('country')}
                                onCheckedChange={() => handleGroupingToggle('country')}
                            />
                            <label
                                className="text-sm font-medium cursor-pointer"
                                onClick={() => handleGroupingToggle('country')}
                            >
                                Por País
                            </label>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {groupingStats.countries}
                        </Badge>
                    </div>

                    {/* Agrupar por Tipo de Pedido */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${groupingFields.has('order_type')
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-accent/30'
                        }`}>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={groupingFields.has('order_type')}
                                onCheckedChange={() => handleGroupingToggle('order_type')}
                            />
                            <label
                                className="text-sm font-medium cursor-pointer"
                                onClick={() => handleGroupingToggle('order_type')}
                            >
                                Por Tipo de Pedido
                            </label>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {groupingStats.orderTypes}
                        </Badge>
                    </div>

                    {/* Agrupar por Equipo */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${groupingFields.has('team_name')
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-accent/30'
                        }`}>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={groupingFields.has('team_name')}
                                onCheckedChange={() => handleGroupingToggle('team_name')}
                            />
                            <label
                                className="text-sm font-medium cursor-pointer"
                                onClick={() => handleGroupingToggle('team_name')}
                            >
                                Por Equipo
                            </label>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {groupingStats.teamNames}
                        </Badge>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Agrupar</h3>
                {groupingFields.size > 0 && (
                    <button
                        onClick={clearGroupings}
                        className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                    >
                        Limpiar
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-3">
                {/* Agrupar por Mensajería */}
                <div className={`flex items-center justify-between p-3 md:p-0 rounded-lg md:rounded-none border md:border-0 transition-colors ${groupingFields.has('courier')
                    ? 'border-primary bg-primary/10 md:bg-transparent'
                    : 'border-input md:border-transparent hover:bg-accent/30 md:hover:bg-transparent'
                    }`}>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={groupingFields.has('courier')}
                            onCheckedChange={() => handleGroupingToggle('courier')}
                        />
                        <label
                            className="text-sm font-medium cursor-pointer"
                            onClick={() => handleGroupingToggle('courier')}
                        >
                            Por Mensajería
                        </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                        {groupingStats.couriers}
                    </Badge>
                </div>

                {/* Agrupar por Tienda */}
                <div className={`flex items-center justify-between p-3 md:p-0 rounded-lg md:rounded-none border md:border-0 transition-colors ${groupingFields.has('shop')
                    ? 'border-primary bg-primary/10 md:bg-transparent'
                    : 'border-input md:border-transparent hover:bg-accent/30 md:hover:bg-transparent'
                    }`}>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={groupingFields.has('shop')}
                            onCheckedChange={() => handleGroupingToggle('shop')}
                        />
                        <label
                            className="text-sm font-medium cursor-pointer"
                            onClick={() => handleGroupingToggle('shop')}
                        >
                            Por Tienda
                        </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                        {groupingStats.shops}
                    </Badge>
                </div>

                {/* Agrupar por País */}
                <div className={`flex items-center justify-between p-3 md:p-0 rounded-lg md:rounded-none border md:border-0 transition-colors ${groupingFields.has('country')
                    ? 'border-primary bg-primary/10 md:bg-transparent'
                    : 'border-input md:border-transparent hover:bg-accent/30 md:hover:bg-transparent'
                    }`}>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={groupingFields.has('country')}
                            onCheckedChange={() => handleGroupingToggle('country')}
                        />
                        <label
                            className="text-sm font-medium cursor-pointer"
                            onClick={() => handleGroupingToggle('country')}
                        >
                            Por País
                        </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                        {groupingStats.countries}
                    </Badge>
                </div>

                {/* Agrupar por Tipo de Pedido */}
                <div className={`flex items-center justify-between p-3 md:p-0 rounded-lg md:rounded-none border md:border-0 transition-colors ${groupingFields.has('order_type')
                    ? 'border-primary bg-primary/10 md:bg-transparent'
                    : 'border-input md:border-transparent hover:bg-accent/30 md:hover:bg-transparent'
                    }`}>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={groupingFields.has('order_type')}
                            onCheckedChange={() => handleGroupingToggle('order_type')}
                        />
                        <label
                            className="text-sm font-medium cursor-pointer"
                            onClick={() => handleGroupingToggle('order_type')}
                        >
                            Por Tipo de Pedido
                        </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                        {groupingStats.orderTypes}
                    </Badge>
                </div>

                {/* Agrupar por Equipo */}
                <div className={`flex items-center justify-between p-3 md:p-0 rounded-lg md:rounded-none border md:border-0 transition-colors ${groupingFields.has('team_name')
                    ? 'border-primary bg-primary/10 md:bg-transparent'
                    : 'border-input md:border-transparent hover:bg-accent/30 md:hover:bg-transparent'
                    }`}>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={groupingFields.has('team_name')}
                            onCheckedChange={() => handleGroupingToggle('team_name')}
                        />
                        <label
                            className="text-sm font-medium cursor-pointer"
                            onClick={() => handleGroupingToggle('team_name')}
                        >
                            Por Equipo
                        </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                        {groupingStats.teamNames}
                    </Badge>
                </div>
            </div>
        </div>
    );
}; 